import type {Moment} from 'moment-timezone'
import {exhaustive} from '@freckle/exhaustive'
import type {NonEmptyArray} from '@freckle/non-empty'
import {nonEmptyToArray} from '@freckle/non-empty'
import type {ProductT} from '@freckle/educator-entities/ts/common/helpers/products'
import type {SubjectT} from '@freckle/educator-entities/ts/common/helpers/subjects'
import {fancyClient, ensureOk, extractData} from '@freckle/educator-entities/ts/openapi-clients'
import {productToPracticeTypeFilter} from '@freckle/school/ts/common/helpers/practice-type'

type DashboardSubjectT = 'math' | 'ela_skills' // Subject enums for Periscope dashboards

type AllOrSpecificT<T> =
  | {
      tag: 'all'
    }
  | {
      tag: 'specific'
      specific: NonEmptyArray<T>
    }

type AllOrNoneOrSpecificT<T> =
  | {
      tag: 'all'
    }
  | {
      tag: 'none'
    }
  | {
      tag: 'specific'
      specific: NonEmptyArray<T>
    }

export type AllProvidersOrSpecificT = AllOrSpecificT<DashboardIdentityProviderT>
export type PracticeTypeFilterT = AllOrNoneOrSpecificT<ProductT>

export type DashboardIdentityProviderT = 'manual' | 'clever' | 'class-link' | 'csv' | 'renaissance'
export function allIdentityProviders(): Array<DashboardIdentityProviderT> {
  return ['manual', 'clever', 'class-link', 'csv', 'renaissance']
}

function getDashboardSubject(subject: SubjectT): DashboardSubjectT {
  switch (subject) {
    case 'math':
      return 'math'
    case 'ela':
      return 'ela_skills'
    default:
      throw new Error(`No DashboardSubjectT for subject: ${subject}`)
  }
}

function parsePeriscopeDateRange(
  startDate: Moment,
  endDate: Moment,
): {
  start: number
  end: number
  aggregation?: 'daily' | 'hourly' | 'weekly' | 'monthly' | 'quarterly' | 'yearly'
} {
  const [start, end] = [startDate.valueOf(), endDate.valueOf()]
  const shouldAggregateDaily = startDate.diff(endDate, 'months') === 0
  return {start, end, aggregation: shouldAggregateDaily ? 'daily' : undefined}
}

const idpOriginsParam = (providers: AllProvidersOrSpecificT): {idpOrigins?: string} => {
  switch (providers.tag) {
    case 'all':
      return {}
    case 'specific':
      return {idpOrigins: nonEmptyToArray(providers.specific).join(',')}
    default:
      return exhaustive(providers)
  }
}

const practiceTypeParam = (practiceTypes: PracticeTypeFilterT): {practiceType?: string} => {
  switch (practiceTypes.tag) {
    case 'all':
      return {}
    case 'none':
      return {}
    case 'specific':
      return {
        practiceType: nonEmptyToArray(practiceTypes.specific)
          .map(productToPracticeTypeFilter)
          .join(','),
      }
    default:
      return exhaustive(practiceTypes)
  }
}

type PeriscopeGrowthParams = {
  subject: DashboardSubjectT
  start: number
  end: number
  grade?: number
  domain?: string
  idpOrigins?: string
  aggregation?: 'daily' | 'hourly' | 'weekly' | 'monthly' | 'quarterly' | 'yearly'
}

function parsePeriscopeGrowthParams(
  subject: SubjectT,
  idpOrigins: AllProvidersOrSpecificT,
  startDate: Moment,
  endDate: Moment,
  grade?: number | null,
  domain?: string | null,
): PeriscopeGrowthParams {
  const gradeParam = grade !== null && grade !== undefined ? {grade} : null
  const domainParam = domain !== null && domain !== undefined ? {domain} : null
  return {
    subject: getDashboardSubject(subject),
    ...gradeParam,
    ...domainParam,
    ...idpOriginsParam(idpOrigins),
    ...parsePeriscopeDateRange(startDate, endDate),
  }
}

type RegisterT = {
  email: string
  emailConfirmation: string
  firstName: string
  lastName: string
  position: string
  schoolId: number
}

export default {
  async impersonateTeacher(
    teacherId: number,
    schoolAdminId: number,
    schoolAdminPassword?: string | null,
  ): Promise<void> {
    await fancyClient
      .POST('/2/teachers/sessions/school-admin-impersonations', {
        body: {
          teacherId,
          schoolAdminId,
          schoolAdminPassword,
        },
      })
      .then(ensureOk)
  },

  async inviteTeacher(name: string, email: string): Promise<void> {
    await fancyClient
      .POST('/2/teachers/invite', {
        body: {name, email},
      })
      .then(ensureOk)
  },

  getPeriscopeEmbedSchoolsUsage(
    schoolId: number | undefined | null,
    gradeLevel: number | undefined | null,
    idpOrigins: AllProvidersOrSpecificT,
    practiceTypes: PracticeTypeFilterT,
    startDate: Moment,
    endDate: Moment,
  ): Promise<unknown> {
    const dateRangeParams = parsePeriscopeDateRange(startDate, endDate)
    return fancyClient
      .GET('/2/periscope-embeds/school-usage', {
        params: {
          query: {
            gradeLevel: gradeLevel?.toString(),
            schoolId: schoolId ?? undefined,
            ...idpOriginsParam(idpOrigins),
            ...practiceTypeParam(practiceTypes),
            ...dateRangeParams,
          },
        },
      })
      .then(extractData)
  },

  getPeriscopeEmbedDistrictGrowth(
    subject: SubjectT,
    idpOrigins: AllProvidersOrSpecificT,
    startDate: Moment,
    endDate: Moment,
    grade?: number | null,
    domain?: string | null,
  ): Promise<unknown> {
    return fancyClient
      .GET('/2/periscope-embeds/districts/growth', {
        params: {
          query: parsePeriscopeGrowthParams(subject, idpOrigins, startDate, endDate, grade, domain),
        },
      })
      .then(extractData)
  },

  getPeriscopeEmbedSchoolGrowth(
    subject: SubjectT,
    schoolId: number,
    idpOrigins: AllProvidersOrSpecificT,
    startDate: Moment,
    endDate: Moment,
    grade?: number | null,
    domain?: string | null,
  ): Promise<unknown> {
    return fancyClient
      .GET('/2/periscope-embeds/schools/{PeriscopeEmbedsSchoolGrowthR-SchoolId}/growth', {
        params: {
          path: {
            'PeriscopeEmbedsSchoolGrowthR-SchoolId': schoolId,
          },
          query: parsePeriscopeGrowthParams(subject, idpOrigins, startDate, endDate, grade, domain),
        },
      })
      .then(extractData)
  },

  getPeriscopeEmbedTeacherGrowth(
    subject: SubjectT,
    teacherId: number,
    idpOrigins: AllProvidersOrSpecificT,
    startDate: Moment,
    endDate: Moment,
    grade?: number | null,
    domain?: string | null,
  ): Promise<unknown> {
    return fancyClient
      .GET('/2/periscope-embeds/teachers/{PeriscopeEmbedsTeacherGrowthR-TeacherId}/growth', {
        params: {
          path: {
            'PeriscopeEmbedsTeacherGrowthR-TeacherId': teacherId,
          },
          query: parsePeriscopeGrowthParams(subject, idpOrigins, startDate, endDate, grade, domain),
        },
      })
      .then(extractData)
  },

  getPeriscopeEmbedStudentGrowth(
    subject: SubjectT,
    studentId: number,
    idpOrigins: AllProvidersOrSpecificT,
    startDate: Moment,
    endDate: Moment,
    domain?: string | null,
  ): Promise<unknown> {
    return fancyClient
      .GET('/2/periscope-embeds/students/{PeriscopeEmbedsStudentGrowthR-StudentId}/growth', {
        params: {
          path: {
            'PeriscopeEmbedsStudentGrowthR-StudentId': studentId,
          },
          query: parsePeriscopeGrowthParams(subject, idpOrigins, startDate, endDate, null, domain),
        },
      })
      .then(extractData)
  },

  // school-web has to login to both fancy-api and school-api
  checkSessionCookies(): Promise<void> {
    return fancyClient.GET('/2/school-admins/sessions').then(ensureOk)
  },

  deleteSessionCookies(): Promise<string | null> {
    return fancyClient
      .DELETE('/2/school-admins/sessions')
      .then(extractData)
      .then(data => data.redirectUrl ?? null)
  },

  async addSchoolAssessment(schoolId: number, studentIds: Array<number>): Promise<void> {
    await fancyClient
      .POST('/2/schools/{SchoolP-SchoolId}/assessments', {
        params: {
          path: {
            'SchoolP-SchoolId': schoolId,
          },
        },
        body: {studentIds},
      })
      .then(ensureOk)
  },

  async login(email: string, password: string): Promise<void> {
    await fancyClient.POST('/2/school-admins/sessions', {
      body: {email, password},
    })
  },

  reloginAsTeacher(): Promise<{
    redirectUrl: string
  }> {
    return fancyClient.POST('/2/school-admins/teacher-sessions').then(extractData)
  },

  async sendEmailReset(email: string): Promise<void> {
    await fancyClient
      .POST('/2/school-admins/forgot-password', {
        body: {email},
      })
      .then(ensureOk)
  },

  async register(data: RegisterT): Promise<void> {
    await fancyClient
      .POST('/2/school-admins', {
        body: data,
      })
      .then(ensureOk)
  },

  async setPassword(schoolAdminId: number, newPassword: string): Promise<void> {
    await fancyClient
      .PATCH('/2/school-admins/{SchoolAdminP-SchoolAdminId}/credentials', {
        params: {
          path: {
            'SchoolAdminP-SchoolAdminId': schoolAdminId,
          },
        },
        body: {newPassword},
      })
      .then(ensureOk)
  },
}
